import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Chip,
  Dialog,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/SearchOutlined"
import TaskIcon from "@mui/icons-material/TaskAltOutlined"
import ProjectIcon from "@mui/icons-material/FolderSpecialOutlined"

import { taskApi, projectApi } from "../api"
import { usePermissions } from "../permissions"
import { visibleNavSections } from "./navConfig"

/**
 * Ctrl-K: go anywhere, find anything.
 *
 * ── Why a palette on top of a perfectly good sidebar ────────────────────────
 *
 * The sidebar is eleven destinations. The app is eleven destinations plus
 * several thousand tasks and projects, and the only route to one of those is to
 * guess which list it is on, go there, and filter. Somebody who knows they want
 * RECON-14 should be able to say so.
 *
 * ── Why it searches the server ──────────────────────────────────────────────
 *
 * A palette that only matches what the current page has loaded is a palette
 * that cannot find anything you are not already looking at — which is the case
 * it exists for. Pages are the local half and stay instant; records are fetched,
 * debounced.
 *
 * ── Why the results are not ranked together ─────────────────────────────────
 *
 * Pages, tasks and projects are three kinds of answer, and interleaving them by
 * some blended score means the destination somebody wanted moves every time
 * they type another letter. Grouped and in a fixed order, the first row is
 * predictable — which is what makes it safe to press Enter without reading.
 */
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const [query, setQuery] = useState("")
  const [records, setRecords] = useState({ tasks: [], projects: [] })
  const [active, setActive] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setQuery("")
      setRecords({ tasks: [], projects: [] })
      setActive(0)
    }
  }, [open])

  /* Every destination this account can actually reach — the same permission
     filter the sidebar uses, so the palette cannot offer a page that 403s. */
  const pages = useMemo(
    () =>
      visibleNavSections(can)
        .flatMap((section) => section.items)
        .map((item) => ({ id: `page:${item.id}`, label: item.label, to: item.to, icon: item.icon })),
    [can],
  )

  const matchedPages = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return pages
    return pages.filter((page) => page.label.toLowerCase().includes(term))
  }, [pages, query])

  useEffect(() => {
    const term = query.trim()
    if (!open || term.length < 2) {
      setRecords({ tasks: [], projects: [] })
      return undefined
    }
    /* Debounced, because this is two requests per keystroke otherwise — and a
       palette that lags behind the typing is one people stop trusting. */
    const timer = setTimeout(async () => {
      const [tasks, projects] = await Promise.all([
        can("tasks", "view") ? taskApi.list({ q: term, limit: 5 }).catch(() => null) : null,
        can("projects", "view") ? projectApi.list({ q: term, limit: 5 }).catch(() => null) : null,
      ])
      setRecords({ tasks: tasks?.tasks?.slice(0, 5) || [], projects: projects?.projects?.slice(0, 5) || [] })
    }, 250)
    return () => clearTimeout(timer)
  }, [query, open, can])

  /* One flat list for the keyboard, in the order they are drawn. The grouping
     is visual; arrow keys have to cross the group boundaries or the rows below
     the first heading are unreachable without a mouse. */
  const rows = useMemo(
    () => [
      ...matchedPages.map((page) => ({ ...page, group: "Pages", run: () => navigate(page.to) })),
      ...records.tasks.map((task) => ({
        id: `task:${task.id}`,
        label: task.task_code ? `${task.task_code}: ${task.title}` : task.title,
        caption: task.project_name || task.client_label,
        icon: TaskIcon,
        group: "Tasks",
        run: () => navigate(`/tasks?open=${task.id}`),
      })),
      ...records.projects.map((project) => ({
        id: `project:${project.id}`,
        label: project.code ? `${project.code} · ${project.name}` : project.name,
        caption: project.client_label,
        icon: ProjectIcon,
        group: "Projects",
        run: () => navigate(`/projects/${project.id}`),
      })),
    ],
    [matchedPages, records, navigate],
  )

  useEffect(() => {
    setActive((current) => Math.min(current, Math.max(0, rows.length - 1)))
  }, [rows.length])

  const choose = useCallback(
    (row) => {
      if (!row) return
      row.run()
      onClose()
    },
    [onClose],
  )

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActive((current) => (current + 1) % Math.max(1, rows.length))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setActive((current) => (current - 1 + rows.length) % Math.max(1, rows.length))
    } else if (event.key === "Enter") {
      event.preventDefault()
      choose(rows[active])
    }
  }

  let lastGroup = null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      /* Near the top rather than centred: the list grows downwards, and a
         centred dialog that grows moves the row under the cursor. */
      slotProps={{ paper: { sx: { position: "fixed", top: 80, m: 0, borderRadius: 3, overflow: "hidden" } } }}
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        <SearchIcon sx={{ color: "text.disabled" }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Go to a page, or search tasks and projects…"
          fullWidth
          inputProps={{ "aria-label": "Search pages, tasks and projects" }}
          sx={{ fontSize: 15 }}
        />
        <Chip size="small" label="Esc" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
      </Stack>

      <Divider />

      <List dense sx={{ maxHeight: 380, overflowY: "auto", py: 0.5 }}>
        {rows.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {query.trim().length < 2 ? "Type to search" : "Nothing matches"}
            </Typography>
          </Box>
        ) : (
          rows.map((row, index) => {
            const heading = row.group !== lastGroup ? row.group : null
            lastGroup = row.group
            const Icon = row.icon
            return (
              <Box key={row.id}>
                {heading ? (
                  <Typography
                    variant="caption"
                    sx={{ px: 2, pt: 1, pb: 0.5, display: "block", fontWeight: 700, color: "text.secondary" }}
                  >
                    {heading}
                  </Typography>
                ) : null}
                <ListItemButton
                  selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(row)}
                  sx={{ mx: 1, borderRadius: 1.5 }}
                >
                  {Icon ? (
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <Icon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                  ) : null}
                  <ListItemText
                    primary={row.label}
                    secondary={row.caption}
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                    secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                  />
                </ListItemButton>
              </Box>
            )
          })
        )}
      </List>
    </Dialog>
  )
}
